import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { enumLabel } from '../lib/enumLabel';
import {
  AlertTriangle,
  Clock,
  GitCompare,
  Minus,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShoppingBag,
  Truck,
  X,
} from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { cn } from '../utils/cn';
import { formatEUR } from '../lib/format';

/** One supplier's answer for one product — the engine's offer, whole. */
interface Offer {
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  priority: number;
  supplierArticleNo: string | null;
  status: 'OK' | 'UNAVAILABLE' | 'CIRCUIT_OPEN' | 'TIMEOUT' | 'MANUAL_ENTRY' | 'API_ERROR';
  unitPriceHT: number | null;
  availableQty: number | null;
  leadTimeDays: number | null;
  cutoffTime: string | null;
  cutoffPassed: boolean;
  freeShippingThreshold: number | null;
  minOrderValue: number | null;
  totalDeliveredHT: number | null;
  bestPrice: boolean;
  source: 'API' | 'MANUAL';
  available: boolean;
  manual: boolean;
}

interface CompareRow {
  /** A product we carry — or null for a REFERENCE row, a part we don't, compared by number. */
  productId: string | null;
  /** The manufacturer number a reference row was compared by; null on a product row. */
  reference: string | null;
  sku: string | null;
  name: string | null;
  brand: string | null;
  internalStock: number;
  offers: Offer[];
}

interface CatalogHit {
  /** A product UUID for CATALOG rows; the synthetic `sup:CODE:REF` key for SUPPLIER rows. */
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  stock: number;
  /** CATALOG = a product we hold. SUPPLIER = not in our catalogue, offered live by a supplier. */
  source: 'CATALOG' | 'SUPPLIER';
  supplier?: { supplierCode: string; supplierArticleNo: string | null };
}

/**
 * A part queued for comparison, with the quantity a PO would be raised for.
 *
 * Two kinds. A PRODUCT line is a row we carry, compared by id. A REFERENCE line is a supplier-only
 * row off the search — a part we don't carry — compared by manufacturer number, so that looking
 * at it writes nothing: the product record is minted only when a PO is placed on an offer, by
 * the replenishment endpoint. (Resolving it to an unlisted record up front, the way a sale does,
 * is a catalogue write for a look — exactly the dead-reference source this desk must not be.)
 */
interface Line {
  /** Row key: the product id, or `ref:<number>` for a reference line. */
  key: string;
  productId: string | null;
  reference: string | null;
  sku: string;
  name: string;
  brand: string | null;
  qty: number;
}

/** A price/lead time the buyer typed for a non-API supplier, keyed row key → supplierId. */
type ManualDraft = Record<string, Record<string, { price: string; lead: string }>>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The one key both the basket and the result rows are addressed by. */
const rowKey = (r: { productId: string | null; reference: string | null }): string =>
  r.productId ?? `ref:${(r.reference ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
const eur = (n: number): string => formatEUR(n);

/**
 * Why an offer has no price. The engine distinguishes these; collapsing them into "unavailable"
 * (as this screen used to) tells a buyer nothing about whether to wait, call, or buy elsewhere.
 */
const STATUS_TONE: Record<Offer['status'], 'success' | 'warning' | 'destructive' | 'muted'> = {
  OK: 'success',
  MANUAL_ENTRY: 'muted',
  UNAVAILABLE: 'muted',
  TIMEOUT: 'warning',
  CIRCUIT_OPEN: 'warning',
  API_ERROR: 'destructive',
};

/** Tone is presentation and stays here; the label and the explanation are copy. */
function statusMeta(status: Offer['status']) {
  return {
    tone: STATUS_TONE[status],
    label: i18n.t(`comparison.status.${status}.label`, { defaultValue: status }),
    hint: i18n.t(`comparison.status.${status}.hint`, { defaultValue: '' }),
  };
}

/**
 * Screen 46 — Supplier comparison.
 *
 * Pick the parts to source, compare every supplier side by side, raise a PO on the winner.
 * The old screen took a comma-separated list of raw product UUIDs and rendered five near-empty
 * columns; the work here is making the same engine legible: search by reference instead of
 * pasting ids, and show WHY an offer has no price (timeout / breaker open / genuinely out of
 * stock), what the delivery actually costs in time (lead + cut-off), and which offer the engine
 * itself picked as best.
 */
export default function Comparison() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canPlacePO = usePermission(Permission.PO_PLACE);

  const [lines, setLines] = useState<Line[]>([]);
  const [rows, setRows] = useState<CompareRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState<ManualDraft>({});
  const [placing, setPlacing] = useState<string | null>(null);

  // ---- product picker ----
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CatalogHit[]>([]);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const lastQ = useRef('');

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    lastQ.current = q;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get<CatalogHit[]>(
          // supplier=1: the purchasing desk is the other legitimate consumer of the quota.
          `/catalog/search?q=${encodeURIComponent(q)}&mode=ALL&supplier=1`
        );
        if (lastQ.current === q) setHits(data.slice(0, 8));
      } catch (err) {
        showApiError(err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setHits([]);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const addLine = (l: Omit<Line, 'key'>): void => {
    const key = rowKey(l);
    setLines((cur) => (cur.some((x) => x.key === key) ? cur : [...cur, { ...l, key }]));
    setQ('');
    setHits([]);
  };

  /* A SUPPLIER row carries the search's synthetic `sup:CODE:REF` key, not a product id. It is
     queued by its manufacturer number (the row's sku) and compared through the by-number lane —
     nothing is written for it until a PO is placed. See Line. */
  const addHit = (h: CatalogHit): void =>
    addLine(
      h.source === 'SUPPLIER'
        ? { productId: null, reference: h.sku, sku: h.sku, name: h.name, brand: h.brand, qty: 1 }
        : { productId: h.id, reference: null, sku: h.sku, name: h.name, brand: h.brand, qty: 1 }
    );

  /** Pasting a product UUID still works — that was the only way in before. */
  const addRaw = async (): Promise<void> => {
    const id = q.trim();
    if (!UUID.test(id)) return;
    try {
      // The detail projection has no `stock` field — only what a basket line needs.
      const { data } = await apiClient.get<Omit<CatalogHit, 'stock'>>(`/catalog/products/${id}`);
      addLine({
        productId: data.id,
        reference: null,
        sku: data.sku,
        name: data.name,
        brand: data.brand,
        qty: 1,
      });
    } catch (err) {
      showApiError(err);
    }
  };

  const setQty = (key: string, qty: number): void =>
    setLines((cur) => cur.map((l) => (l.key === key ? { ...l, qty: Math.max(1, qty) } : l)));
  const removeLine = (key: string): void => setLines((cur) => cur.filter((l) => l.key !== key));

  // ---- compare ----
  const manualOffers = useMemo(
    () =>
      Object.entries(manual).flatMap(([key, bySupplier]) => {
        const line = lines.find((l) => l.key === key);
        if (!line) return [];
        return Object.entries(bySupplier)
          .filter(([, v]) => Number(v.price) > 0)
          .map(([supplierId, v]) => ({
            ...(line.productId ? { productId: line.productId } : { reference: line.reference }),
            supplierId,
            unitPriceHT: Number(v.price),
            available: true,
            leadTimeDays: Math.max(0, Number(v.lead) || 0),
          }));
      }),
    [manual, lines]
  );

  const compare = async (): Promise<void> => {
    if (lines.length === 0) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<CompareRow[]>(
        '/purchasing/compare',
        {
          productIds: lines.flatMap((l) => (l.productId ? [l.productId] : [])),
          references: lines.flatMap((l) =>
            l.reference ? [{ number: l.reference, brand: l.brand, name: l.name }] : []
          ),
          manualOffers,
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setRows(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const createPO = async (row: CompareRow, offer: Offer): Promise<void> => {
    const key = rowKey(row);
    const qty = lines.find((l) => l.key === key)?.qty ?? 1;
    setPlacing(`${key}|${offer.supplierId}`);
    try {
      const headers = { 'X-Command-Id': crypto.randomUUID() };
      const { data } = row.productId
        ? await apiClient.post<{ poNumber: string; status: string }>(
            '/purchasing/orders',
            {
              supplierId: offer.supplierId,
              lines: [
                {
                  productId: row.productId,
                  quantity: qty,
                  unitPriceHT: offer.unitPriceHT ?? 0,
                  ...(offer.leadTimeDays != null ? { leadTimeDays: offer.leadTimeDays } : {}),
                },
              ],
            },
            { headers }
          )
        : // A reference row has no product record yet. Replenishment mints it HERE, at the
          // moment the buyer commits — matched to an existing row by reference first.
          await apiClient.post<{ poNumber: string; status: string }>(
            '/purchasing/replenishment',
            {
              supplierCode: offer.supplierCode,
              lines: [
                {
                  reference: row.reference,
                  ...(offer.supplierArticleNo
                    ? { supplierArticleNo: offer.supplierArticleNo }
                    : {}),
                  quantity: qty,
                  unitPriceHT: offer.unitPriceHT ?? 0,
                },
              ],
            },
            { headers }
          );
      push({
        tone: 'success',
        title: `${data.poNumber} — ${enumLabel('poStatus', data.status)}`,
        message: t('comparison.poCreated', {
          qty,
          part: row.sku ?? t('comparison.part'),
          supplier: offer.supplierName,
        }),
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setPlacing(null);
    }
  };

  const stale = rows !== null && rows.length !== lines.length;

  return (
    <div className="space-y-5">
      <PageHeader title={t('comparison.title')} description={t('comparison.description')} />

      {/* ---- basket ---- */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div ref={boxRef} className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              value={q}
              placeholder={t('comparison.searchPlaceholder')}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void addRaw()}
            />
          </div>
          {(hits.length > 0 || (searching && q.trim().length >= 2)) && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
              {searching && hits.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {t('common.searching')}
                </div>
              )}
              {hits.map((h) => (
                <button
                  key={h.id}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => addHit(h)}
                >
                  <span className="min-w-0">
                    <span className="font-mono">{h.sku}</span>
                    <span className="ml-2 text-muted-foreground">{h.name}</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {h.source === 'SUPPLIER'
                      ? t('comparison.hitSupplier', {
                          brand: h.brand ?? '—',
                          supplier: h.supplier?.supplierCode ?? '',
                        })
                      : t('comparison.hitStock', { brand: h.brand ?? '—', stock: h.stock })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('comparison.emptyBasket')}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {lines.map((l) => (
              <li key={l.key} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">
                    <span className="font-mono">{l.sku}</span>
                    <span className="ml-2 text-muted-foreground">{l.name}</span>
                    {l.reference && (
                      <Badge variant="muted" className="ml-2" title={t('comparison.referenceHint')}>
                        {t('comparison.reference')}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{l.brand ?? '—'}</div>
                </div>
                <div className="flex items-center gap-1" title={t('comparison.qtyTitle')}>
                  <Button size="sm" variant="ghost" onClick={() => setQty(l.key, l.qty - 1)}>
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="w-8 text-center text-sm tabular-nums">{l.qty}</span>
                  <Button size="sm" variant="ghost" onClick={() => setQty(l.key, l.qty + 1)}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeLine(l.key)}>
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={() => void compare()} disabled={busy || lines.length === 0}>
            {busy ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <GitCompare className="h-4 w-4" />
            )}
            {busy ? t('comparison.consulting') : t('comparison.compare')}
          </Button>
          {manualOffers.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {t('comparison.manualIncluded', { count: manualOffers.length })}
            </span>
          )}
          {stale && (
            <span className="flex items-center gap-1.5 text-xs text-warning">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('comparison.stale')}
            </span>
          )}
        </div>
      </section>

      {/* ---- results ---- */}
      {rows === null ? (
        <section className="rounded-lg border border-dashed bg-card p-10 text-center">
          <GitCompare className="mx-auto h-7 w-7 text-muted-foreground" />
          <h2 className="mt-3 font-medium">{t('comparison.nothingCompared')}</h2>
          <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
            {t('comparison.nothingComparedHint')}
          </p>
        </section>
      ) : (
        rows.map((row) => (
          <ProductResult
            key={rowKey(row)}
            row={row}
            qty={lines.find((l) => l.key === rowKey(row))?.qty ?? 1}
            canPlacePO={canPlacePO}
            placing={placing}
            manual={manual[rowKey(row)] ?? {}}
            onManual={(supplierId, patch) =>
              setManual((cur) => ({
                ...cur,
                [rowKey(row)]: {
                  ...(cur[rowKey(row)] ?? {}),
                  [supplierId]: {
                    ...(cur[rowKey(row)]?.[supplierId] ?? { price: '', lead: '' }),
                    ...patch,
                  },
                },
              }))
            }
            onCreatePO={(offer) => void createPO(row, offer)}
          />
        ))
      )}
    </div>
  );
}

// ===================== one product's offer table =====================
function ProductResult({
  row,
  qty,
  canPlacePO,
  placing,
  manual,
  onManual,
  onCreatePO,
}: {
  row: CompareRow;
  qty: number;
  canPlacePO: boolean;
  placing: string | null;
  manual: Record<string, { price: string; lead: string }>;
  onManual: (supplierId: string, patch: Partial<{ price: string; lead: string }>) => void;
  onCreatePO: (offer: Offer) => void;
}) {
  const { t } = useTranslation();
  // Quotable offers first (cheapest delivered), then everything that could not answer — a buyer
  // scans top-down and should never have to hunt past five dead rows for the one real price.
  const offers = useMemo(
    () =>
      [...row.offers].sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (a.available) {
          return (
            (a.totalDeliveredHT ?? a.unitPriceHT ?? 0) - (b.totalDeliveredHT ?? b.unitPriceHT ?? 0)
          );
        }
        return a.priority - b.priority;
      }),
    [row.offers]
  );
  const quotable = offers.filter((o) => o.available).length;

  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-semibold">{row.sku ?? row.productId}</span>
            {row.brand && <Badge variant="secondary">{row.brand}</Badge>}
            {row.reference && (
              <Badge variant="muted" title={t('comparison.referenceHint')}>
                {t('comparison.reference')}
              </Badge>
            )}
          </div>
          <p className="truncate text-sm text-muted-foreground">{row.name ?? '—'}</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5" />
            {row.internalStock > 0
              ? t('comparison.inDepot', { count: row.internalStock })
              : t('comparison.notInDepot')}
          </span>
          <span>
            {t('comparison.sourcing')} <b className="tabular-nums">{qty}</b>
          </span>
          <span>{t('comparison.quotedCount', { quoted: quotable, total: offers.length })}</span>
        </div>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left font-medium">
                {t('comparison.columns.supplier')}
              </th>
              <th className="px-3 py-2 text-left font-medium">{t('common.status')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('comparison.columns.unitHt')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('comparison.columns.forQty', { qty })}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('comparison.columns.availability')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('comparison.columns.delivery')}
              </th>
              <th className="px-3 py-2 text-left font-medium">{t('comparison.columns.terms')}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => {
              const meta = statusMeta(o.status);
              const key = `${rowKey(row)}|${o.supplierId}`;
              const unit = o.unitPriceHT ?? 0;
              return (
                <tr
                  key={o.supplierId}
                  className={cn('border-t align-top', o.bestPrice && 'bg-success/10')}
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{o.supplierName}</span>
                      {o.bestPrice && <Badge variant="success">{t('comparison.bestPrice')}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {o.supplierCode} {t('comparison.priority', { priority: o.priority })}
                      {o.supplierArticleNo &&
                        t('comparison.theirRef', { ref: o.supplierArticleNo })}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={meta.tone} title={meta.hint}>
                      {meta.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {o.manual && o.status !== 'OK' ? (
                      <ManualEntry
                        draft={manual[o.supplierId] ?? { price: '', lead: '' }}
                        onChange={(patch) => onManual(o.supplierId, patch)}
                      />
                    ) : unit > 0 ? (
                      eur(unit)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {unit > 0 ? eur(unit * qty) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {o.availableQty != null && o.availableQty > 0 ? (
                      <span className={o.availableQty < qty ? 'text-warning' : undefined}>
                        {t('comparison.availableQty', { count: o.availableQty })}
                        {o.availableQty < qty && t('comparison.shortSuffix')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {o.leadTimeDays != null ? (
                      <div className="flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                        {o.leadTimeDays === 0
                          ? t('comparison.sameDay')
                          : t('comparison.dPlus', { days: o.leadTimeDays })}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {o.cutoffTime && (
                      <div
                        className={cn(
                          'mt-1 flex items-center gap-1.5',
                          o.cutoffPassed ? 'text-warning' : 'text-muted-foreground'
                        )}
                        title={
                          o.cutoffPassed
                            ? t('comparison.cutoffPassedTitle', { time: o.cutoffTime })
                            : t('comparison.cutoffTitle', { time: o.cutoffTime })
                        }
                      >
                        <Clock className="h-3.5 w-3.5" />
                        {o.cutoffTime}
                        {o.cutoffPassed && t('comparison.cutoffPassedSuffix')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {o.minOrderValue != null && (
                      <div>{t('comparison.minOrder', { amount: eur(o.minOrderValue) })}</div>
                    )}
                    {o.freeShippingThreshold != null && (
                      <div>
                        {t('comparison.freeShip', { amount: eur(o.freeShippingThreshold) })}
                      </div>
                    )}
                    {o.minOrderValue == null && o.freeShippingThreshold == null && '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canPlacePO && (
                      <Button
                        size="sm"
                        variant={o.bestPrice ? 'default' : 'outline'}
                        disabled={!o.available || unit <= 0 || placing === key}
                        title={
                          !o.available
                            ? meta.hint
                            : t('comparison.orderTitle', {
                                qty,
                                part: row.sku ?? t('comparison.thisPart'),
                              })
                        }
                        onClick={() => onCreatePO(o)}
                      >
                        <ShoppingBag className="h-4 w-4" />
                        {placing === key ? t('comparison.placing') : t('comparison.order')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {quotable === 0 && (
        <p className="border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          {t('comparison.noQuote')}
        </p>
      )}
    </section>
  );
}

/** Price + lead time for a supplier with no API. Applied on the next comparison run. */
function ManualEntry({
  draft,
  onChange,
}: {
  draft: { price: string; lead: string };
  onChange: (patch: Partial<{ price: string; lead: string }>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-1.5">
      <Input
        className="h-8 w-20 text-right"
        inputMode="decimal"
        placeholder={t('comparison.manualPricePlaceholder')}
        value={draft.price}
        aria-label={t('comparison.manualPriceAria')}
        onChange={(e) => onChange({ price: e.target.value })}
      />
      <Input
        className="h-8 w-14 text-right"
        inputMode="numeric"
        placeholder={t('comparison.manualLeadPlaceholder')}
        value={draft.lead}
        aria-label={t('comparison.manualLeadAria')}
        onChange={(e) => onChange({ lead: e.target.value })}
      />
    </div>
  );
}
